import { Control, Controller, FieldErrors, UseFormSetValue } from "react-hook-form";
import { SupplierLocation } from "../../../interfaces/Purchases/Catalogs/suppliers";
import { Country } from "../../../interfaces/Commons/Locations/countries";
import { State } from "../../../interfaces/Commons/Locations/states";
import { City } from "../../../interfaces/Commons/Locations/cities";
import { useTranslation } from "react-i18next";
import { FormsErrors } from "../../../hooks/Forms/FormsErrors";
import { useEffect, useState } from "react";
import { Autocomplete, Box, Switch, TextField, Typography } from "@mui/material";
import { Divider } from "../../../components/Commons/Divider";
import { emailRegex } from "../../../constants/Regex";
import { TaxRegime } from "../../../interfaces/Sales/Catalogs/taxRegimes";

type UpdateGeneralProps = {
  control: Control<SupplierLocation, any>;
  errors: FieldErrors<SupplierLocation>;
  comboCountries: Country[];
  defaultCountry: Country | undefined;
  comboStates: State[];
  defaultState: State | undefined;
  comboCities: City[];
  defaultCity: City | undefined;
  comboTaxRegimes: TaxRegime[];
  defaultTaxRegime: TaxRegime | undefined;
  isEdit: boolean;
  supplierLocationPayload: SupplierLocation;
  getCities: (stateId: number) => void,
  setValue: UseFormSetValue<SupplierLocation>
}

export const UpdateGeneral = ({
  control,
  errors,
  comboCountries,
  defaultCountry,
  comboStates,
  defaultState,
  comboCities,
  defaultCity,
  comboTaxRegimes,
  defaultTaxRegime,
  supplierLocationPayload,
  isEdit,
  getCities,
  setValue
}: UpdateGeneralProps) => {
  const [t] = useTranslation("global");
  const { GetError } = FormsErrors();

  const [isStateSelected, setIsStateSelected] = useState(false);
  const [stateId, setStateId] = useState<number | undefined>(defaultState?.state_id);

  const [city, setCity] = useState<City | null>()

  useEffect(() => {
    if (stateId !== undefined) {
      setCity(null)
      getCities(stateId)
      setIsStateSelected(true);
      return;
    }
    setIsStateSelected(false);
  }, [stateId])

  useEffect(() => {
    setCity((previewState) => defaultCity);
  }, [])



  return (
    <>
      <div className='d-flex'>
        <div className='w-50'>
          <Controller
            name="country_id"
            control={control}
            rules={{ required: true }}
            render={({ field }) =>
              <Autocomplete
                ref={field.ref}
                size="small"
                options={comboCountries}
                defaultValue={defaultCountry}
                getOptionLabel={(option) => `${option.country_code} - ${option.country_name}`}
                renderOption={(props, option: Country) => (
                  <div key={option.country_id}>
                    <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                      {option.country_code} - {option.country_name}
                    </Box>
                  </div>
                )}
                isOptionEqualToValue={(option, value) => option.country_id === value.country_id}
                onChange={(_, values) => {
                  field.onChange(values?.country_id || null)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`${t("supplierlocations.fields.country")}`}
                    variant="filled"
                    value={field.value}
                    error={!!errors.country_id}
                    helperText={GetError(errors.country_id?.type)}
                    sx={{ paddingRight: 2 }}
                    disabled={isEdit}
                  />
                )}
              />
            }
          />
        </div>

        <div className='w-50'>
          <Controller
            name="state_id"
            control={control}
            rules={{ required: true }}
            render={({ field }) =>
              <Autocomplete
                ref={field.ref}
                size="small"
                options={comboStates}
                defaultValue={defaultState}
                getOptionLabel={(option) => `${option.state_code} - ${option.state_name}`}
                renderOption={(props, option: State) => (
                  <div key={option.state_id}>
                    <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                      {option.state_code} - {option.state_name}
                    </Box>
                  </div>
                )}
                isOptionEqualToValue={(option, value) => option.state_id === value.state_id}
                onChange={(_, values) => {
                  setValue("city_id", undefined);
                  field.onChange(values?.state_id || null);
                  setStateId(values?.state_id);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`${t("supplierlocations.fields.state")}`}
                    variant="filled"
                    value={field.value}
                    error={!!errors.state_id}
                    helperText={GetError(errors.state_id?.type)}
                    disabled={isEdit}
                  />
                )}
              />
            }
          />
        </div>
      </div >

      <div className='d-flex mt-3'>
        <div className='w-50'>
          {!isStateSelected &&
            <Controller
              name="city_id"
              control={control}
              rules={{ required: true }}
              render={({ field }) =>
                <Autocomplete
                  ref={field.ref}
                  size="small"
                  options={comboCities}
                  getOptionLabel={(option) => `${option.city_code} - ${option.city_name}`}
                  renderOption={(props, option: City) => (
                    <div key={option.city_id}>
                      <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                        {option.city_code} - {option.city_name}
                      </Box>
                    </div>
                  )}
                  isOptionEqualToValue={(option, value) => option.city_id === value.city_id}
                  onChange={(_, values) => {
                    field.onChange(values?.city_id || null)
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`${t("supplierlocations.fields.city_name")}`}
                      variant="filled"
                      value={field.value}
                      error={!!errors.city_id}
                      helperText={GetError(errors.city_id?.type)}
                      sx={{ paddingRight: 2 }}

                    />
                  )}
                  disabled
                />
              }
            />
          }
          {isStateSelected &&
            <Controller
              name="city_id"
              control={control}
              rules={{ required: true }}
              render={({ field }) =>
                <Autocomplete
                  ref={field.ref}
                  size="small"
                  options={comboCities}
                  value={city}
                  getOptionLabel={(option) => `${option.city_code} - ${option.city_name}`}
                  renderOption={(props, option: City) => (
                    <div key={option.city_id}>
                      <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                        {option.city_code} - {option.city_name}
                      </Box>
                    </div>
                  )}

                  isOptionEqualToValue={(option, value) => option.city_id === value.city_id}
                  onChange={(_, values) => {
                    setCity(values);
                    field.onChange(values?.city_id || null)
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`${t("supplierlocations.fields.city_name")}`}
                      variant="filled"
                      value={field.value}
                      error={!!errors.city_id}
                      helperText={GetError(errors.city_id?.type)}
                      sx={{ paddingRight: 2 }}
                    />
                  )}
                />
              }
            />
          }
        </div>

        <div className='w-50'>
          <Controller
            name="street"
            control={control}
            rules={{ required: true, maxLength: 60 }}
            render={({ field }) =>
              <TextField
                variant="filled"
                label={`${t("supplierlocations.fields.street")}`}
                ref={field.ref}
                value={field.value}
                onChange={(event) => { field.onChange(event.target.value) }}
                error={!!errors.street}
                helperText={GetError(errors.street?.type)}
                size="small"
                style={{ width: "100%" }}
                disabled={isEdit}
              />
            }
          />
        </div>
      </div>

      <div className='d-flex mt-3'>
        <div className='w-50'>
          <Controller
            name="postal_code"
            control={control}
            rules={{ maxLength: 5, required: true }}
            render={({ field }) =>
              <TextField
                variant="filled"
                label={`${t("supplierlocations.fields.postal_code")}`}
                ref={field.ref}
                type="number"
                sx={{
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                  "& input[type=number]": { MozAppearance: "textfield", }, paddingRight: 2
                }}
                value={field.value}
                onChange={(event) => { field.onChange(event.target.value) }}
                error={!!errors.postal_code}
                helperText={GetError(errors.postal_code?.type)}
                size="small"
                style={{ width: "100%" }}
                disabled={isEdit}
              />
            }
          />
        </div>
      </div>

      <Divider />

      <div className='d-flex'>
        <div className="w-50">
          <Controller
            name="business_name"
            control={control}
            rules={{ required: true, maxLength: 60 }}
            render={({ field }) =>
              <TextField
                variant="filled"
                label={`${t("supplierlocations.fields.business_name")}`}
                ref={field.ref}
                value={field.value}
                onChange={(event) => { field.onChange(event.target.value) }}
                error={!!errors.business_name}
                helperText={GetError(errors.business_name?.type)}
                size="small"
                sx={{ paddingRight: 2 }}
                style={{ width: "100%" }}
                disabled={isEdit}
              />
            }
          />
        </div>
        <div className="w-50">
          <Controller
            name="supplier_rfc"
            control={control}
            rules={{ required: true, maxLength: 13 }}
            render={({ field }) =>
              <TextField
                variant="filled"
                label={`${t("supplierlocations.fields.supplier_rfc")}`}
                ref={field.ref}
                value={field.value}
                onChange={(event) => { field.onChange(event.target.value) }}
                error={!!errors.supplier_rfc}
                helperText={GetError(errors.supplier_rfc?.type)}
                size="small"
                style={{ width: "100%" }}
                disabled={isEdit}
              />
            }
          />
        </div>
      </div>

      <div className="d-flex mt-3">
        <div className="w-50">
          <Controller
            name="tax_regime_id"
            control={control}
            rules={{ required: true }}
            render={({ field }) =>
              <Autocomplete
                ref={field.ref}
                size="small"
                options={comboTaxRegimes}
                defaultValue={defaultTaxRegime}
                getOptionLabel={(option) => `${option.tax_regime_code} - ${option.description}`}
                renderOption={(props, option: TaxRegime) => (
                  <div key={option.tax_regime_id}>
                    <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                      {option.tax_regime_code} - {option.description}
                    </Box>
                  </div>
                )}
                isOptionEqualToValue={(option, value) => option.tax_regime_id === value.tax_regime_id}
                onChange={(_, values) => {
                  field.onChange(values?.tax_regime_id || null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`${t("taxregimes.title-view")}`}
                    variant="filled"
                    value={field.value}
                    error={!!errors.tax_regime_id}
                    helperText={GetError(errors.tax_regime_id?.type)}
                    sx={{ paddingRight: 2 }}
                  />
                )}
              />
            }
          />
        </div>
        <div className="w-50"></div>
      </div>

      <div className='d-flex mt-3'>
        <div className='w-50'>
          <Controller
            name="contact_name"
            control={control}
            rules={{ maxLength: 50 }}
            render={({ field }) =>
              <TextField
                variant="filled"
                label={`${t("supplierlocations.fields.contact_name")}`}
                ref={field.ref}
                value={field.value}
                onChange={(event) => { field.onChange(event.target.value) }}
                error={!!errors.contact_name}
                helperText={GetError(errors.contact_name?.type)}
                size="small"
                style={{ width: "100%" }}
                sx={{ paddingRight: 2 }}
                disabled={isEdit}
              />
            }
          />
        </div>

        <div className='w-50'>
          <Controller
            name="supplier_phone_number"
            control={control}
            rules={{ maxLength: 20 }}
            render={({ field }) =>
              <TextField
                variant="filled"
                label={`${t("supplierlocations.fields.phone_number")}`}
                ref={field.ref}
                value={field.value}
                type="number"
                sx={{
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none", },
                  "& input[type=number]": { MozAppearance: "textfield", },
                }}
                onChange={(event) => { field.onChange(event.target.value) }}
                error={!!errors.supplier_phone_number}
                helperText={GetError(errors.supplier_phone_number?.type)}
                size="small"
                style={{ width: "100%" }}
                disabled={isEdit}
              />
            }
          />
        </div>
      </div>

      <div className='d-flex mt-3'>
        <div className='w-50'>
          <Controller
            name="supplier_email"
            control={control}
            rules={{ maxLength: 1000, pattern: emailRegex }}
            render={({ field }) =>
              <TextField
                variant="filled"
                label={`${t("supplierlocations.fields.email")}`}
                ref={field.ref}
                value={field.value}
                onChange={(event) => { field.onChange(event.target.value) }}
                error={!!errors.supplier_email}
                helperText={GetError(errors.supplier_email?.type)}
                size="small"
                style={{ width: "100%" }}
                sx={{ paddingRight: 2 }}
                disabled={isEdit}
              />
            }
          />
        </div>

        <div className='w-50'>
          <Controller
            name="supplier_web"
            control={control}
            rules={{ maxLength: 100 }}
            render={({ field }) =>
              <TextField
                variant="filled"
                label={`${t("supplierlocations.fields.supplier_web")}`}
                ref={field.ref}
                value={field.value}
                onChange={(event) => { field.onChange(event.target.value) }}
                error={!!errors.supplier_web}
                helperText={GetError(errors.supplier_web?.type)}
                size="small"
                style={{ width: "100%" }}
                disabled={isEdit}
              />
            }
          />
        </div>
      </div>

      <div className="d-flex flex-column mt-3">
        <Controller
          name="fiscal_address"
          control={control}
          render={({ field }) =>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '50%', justifyContent: "space-between" }}>
              <Typography sx={{ ml: 2 }}>{t("supplierlocations.fields.fiscal_address")}</Typography>
              <Switch ref={field.ref} onChange={field.onChange} value={field.value} defaultChecked={supplierLocationPayload.fiscal_address} disabled={isEdit} />
            </Box>
          }
        />
        <Controller
          name="supply_address"
          control={control}
          render={({ field }) =>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '50%', justifyContent: "space-between" }}>
              <Typography sx={{ ml: 2 }}>{t("supplierlocations.fields.supply_address")}</Typography>
              <Switch ref={field.ref} onChange={field.onChange} value={field.value} defaultChecked={supplierLocationPayload.supply_address} disabled={isEdit} />
            </Box>
          }
        />
        <Controller
          name="default_supply_address"
          control={control}
          render={({ field }) =>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '50%', justifyContent: "space-between" }}>
              <Typography sx={{ ml: 2 }}>{t("supplierlocations.fields.default_supply_address")}</Typography>
              <Switch ref={field.ref} onChange={field.onChange} value={field.value} defaultChecked={supplierLocationPayload.default_supply_address} disabled={isEdit} />
            </Box>
          }
        />
      </div>

    </>
  )
}
